import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, UserPlus, Users, Stethoscope, CreditCard, Building2, FlaskConical, BarChart3, UserCheck, Clock, ChevronDown, ChevronRight, Briefcase, Settings, MapPin, Bell, Calendar } from 'lucide-react';
import UserMenu from './UserMenu';
import ClockInOutButton from './ClockInOutButton';
import { LocationSelector } from '@/shared/components/common/LocationSelector';
import { authService } from '@/domains/internal/auth/services/authService';
import { getRoomConfig, getRoomConfigAsync } from '@/config/roomConfig';
import RoomSetupModal from '@/domains/internal/facilities/components/RoomSetupModal';
import RefundApprovalsModal from '@/domains/internal/payments/components/billing/RefundApprovalsModal';
import { usePendingApprovals } from '@/shared/contexts/PendingApprovalsContext';
import { useAuth } from '@/domains/internal/auth/hooks/useAuth';
import { useLayoutData } from '@/shared/contexts/LayoutDataContext';
import { Button } from '@/shared/components/ui/Button';

interface LayoutProps {
  children: React.ReactNode;
}

// Import local Anand Lab logo assets
import imgVector from '@/assets/images/anand-logo-vector1.svg';
import imgVector1 from '@/assets/images/anand-logo-vector2.svg';
import imgVector2 from '@/assets/images/anand-logo-vector3.svg';

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { isSuperAdmin } = useAuth();
  const { pendingCount, refreshCount } = usePendingApprovals();

  // Get room config from LayoutDataContext (fetched in parallel with other layout data)
  const { roomConfig: contextRoomConfig } = useLayoutData();

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    operations: true,
    admin: true,
    reports: true,
  });
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [showApprovalsModal, setShowApprovalsModal] = useState(false);
  const [roomConfig, setRoomConfig] = useState(getRoomConfig());

  // Update local state when context provides room config
  useEffect(() => {
    if (contextRoomConfig) {
      setRoomConfig(contextRoomConfig);
    }
  }, [contextRoomConfig]);

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const handleRoomConfigUpdate = async () => {
    try {
      // Skip cache since user just manually updated room
      const config = await getRoomConfigAsync(true);
      setRoomConfig(config);
    } catch (error) {
      console.error('Failed to refresh room config:', error);
      setRoomConfig(getRoomConfig());
    }
    setShowRoomModal(false);
  };

  const navGroups = [
    {
      type: 'single' as const,
      path: '/dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      description: 'Real-time clinic operations overview',
      requiredRole: null, // Everyone can access
    },
    {
      type: 'single' as const,
      path: '/patients',
      label: 'Patient Registration',
      icon: UserPlus,
      description: 'Register and manage patient information',
      requiredRole: ['receptionist', 'admin', 'super_admin'], // Receptionist and admins
    },
    {
      type: 'group' as const,
      key: 'operations',
      label: 'Operations',
      icon: Briefcase,
      requiredRole: null,
      items: [
        { path: '/queue', label: 'Queue Management', icon: Users, description: 'View and manage patient queue', requiredRole: ['doctor', 'technician', 'admin', 'super_admin'] }, // Doctors, technicians, and admins
        { path: '/doctor-portal', label: 'Doctor Portal', icon: Stethoscope, description: 'Room-based patient service management', requiredRole: ['doctor', 'technician', 'admin', 'super_admin'] }, // Doctors, technicians, and admins
        { path: '/visits', label: 'Visit Management', icon: Stethoscope, description: 'View and manage patient visits and invoices', requiredRole: ['receptionist', 'admin', 'super_admin'] }, // Receptionist and admins
        { path: '/payments', label: 'Billing', icon: CreditCard, description: 'Manage billing, payments, and outstanding invoices', requiredRole: ['receptionist', 'admin', 'super_admin'] }, // Receptionist and admins
      ],
    },
    {
      type: 'group' as const,
      key: 'admin',
      label: 'Admin',
      icon: Settings,
      requiredRole: ['admin', 'super_admin'],
      items: [
        { path: '/staff', label: 'Staff Management', icon: Users, description: 'Manage staff members and roles', requiredRole: ['admin', 'super_admin'] },
        { path: '/time-off-approvals', label: 'Time Off Approvals', icon: Calendar, description: 'Review and approve staff time-off requests', requiredRole: ['admin', 'super_admin'] },
        { path: '/departments', label: 'Departments', icon: Building2, description: 'Manage departments and rooms', requiredRole: ['admin', 'super_admin'] },
        { path: '/tests', label: 'Tests', icon: FlaskConical, description: 'Manage test categories and available tests', requiredRole: ['admin', 'super_admin'] },
        { path: '/referral-management', label: 'Referral Management', icon: UserCheck, description: 'Manage referral sources and commission rates', requiredRole: ['super_admin'] },
      ],
    },
    {
      type: 'group' as const,
      key: 'reports',
      label: 'Reports & Analytics',
      icon: BarChart3,
      requiredRole: ['admin', 'super_admin'],
      items: [
        { path: '/reports', label: 'Reports', icon: BarChart3, description: 'View financial and operational reports', requiredRole: ['admin', 'super_admin'] },
        { path: '/time-tracking', label: 'Time Tracking', icon: Clock, description: 'Monitor staff attendance and work hours', requiredRole: ['admin', 'super_admin'] },
      ],
    },
  ];

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const canShowItem = (requiredRole: string[] | null) => {
    if (!requiredRole) return true;
    return authService.hasAnyRole(requiredRole);
  };

  const getActiveItem = (): { label: string; description: string } | null => {
    for (const group of navGroups) {
      if (group.type === 'single') {
        if (isActive(group.path)) {
          return { label: group.label, description: group.description };
        }
      } else {
        for (const item of group.items) {
          if (isActive(item.path)) {
            return { label: item.label, description: item.description };
          }
        }
      }
    }
    return null;
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar - Exact Figma Implementation */}
      <div
        className="bg-[#f8f7fa] box-border content-stretch flex flex-col items-start relative shadow-[0px_3px_7.5px_0px_rgba(0,0,0,0.23)] w-[240px] h-screen overflow-hidden"
        style={{ boxShadow: '0px 3px 7.5px 0px rgba(0,0,0,0.23)' }}
      >
        {/* Logo Section */}
        <div className="box-border content-stretch flex gap-[8px] h-[118px] items-center leading-[0] px-[20px] py-[24px] relative shrink-0 w-full">
          <div className="grid-cols-[max-content] grid-rows-[max-content] inline-grid place-items-start relative shrink-0">
            <div className="[grid-area:1_/_1] grid-cols-[max-content] grid-rows-[max-content] inline-grid leading-[0] ml-0 mt-0 place-items-start relative">
              {/* Logo Vectors - Exact Figma Assets */}
              <div className="[grid-area:1_/_1] h-[28.155px] ml-[14.102px] mt-0 relative w-[28.594px]">
                <img alt="" className="block max-w-none size-full" src={imgVector} />
              </div>
              <div className="[grid-area:1_/_1] h-[24.886px] ml-[9.834px] mt-[1.533px] relative w-[34.968px]">
                <img alt="" className="block max-w-none size-full" src={imgVector1} />
              </div>
              <div className="[grid-area:1_/_1] h-[4.608px] ml-[9.473px] mt-[23.63px] relative w-[8.906px]">
                <img alt="" className="block max-w-none size-full" src={imgVector2} />
              </div>
              <div className="[grid-area:1_/_1] font-['Inter'] font-normal ml-0 mt-[31.635px] not-italic relative text-[var(--color-primary)] text-[3.218px] text-nowrap">
                <p className="leading-[normal] whitespace-pre">ANAND IMAGING & DIAGNOSTICS</p>
              </div>
            </div>
          </div>
          <div
            className="font-['Roboto'] font-medium relative shrink-0 text-[var(--color-text-primary)] text-[24px] text-nowrap"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            <p className="leading-[32px] whitespace-pre">Anand</p>
          </div>
        </div>

        {/* Navigation Menu */}
        <div className="box-border content-stretch flex flex-col gap-[4px] items-start pb-[12px] pt-[24px] px-[12px] relative w-full flex-1 overflow-y-auto">
          {navGroups.map((group) => {
            if (group.type === 'single') {
              if (!canShowItem(group.requiredRole)) return null;
              const active = isActive(group.path);
              const IconComponent = group.icon;
              return (
                <Link
                  key={group.path}
                  to={group.path}
                  className={`group box-border content-stretch flex gap-[12px] items-center p-[12px] relative rounded-[4px] shrink-0 w-[216px] transition-colors ${
                    active ? 'bg-[var(--color-primary)]' : 'hover:bg-[var(--color-primary-light)]'
                  }`}
                >
                  <div className="relative shrink-0 size-[24px]">
                    <IconComponent className={`w-6 h-6 transition-colors ${
                      active ? 'text-[var(--color-primary-text)]' : 'text-black'
                    }`} />
                  </div>
                  <div
                    className={`font-['Roboto'] font-normal leading-[0] relative text-[15px] tracking-[0.5px] overflow-hidden text-ellipsis transition-colors ${
                      active ? 'text-[var(--color-primary-text)]' : 'text-black'
                    }`}
                    style={{ fontVariationSettings: "'wdth' 100" }}
                  >
                    <p className="leading-[24px] truncate">{group.label}</p>
                  </div>
                </Link>
              );
            } else {
              // Group dropdown
              if (!canShowItem(group.requiredRole)) return null;
              const hasVisibleItems = group.items.some(item => canShowItem(item.requiredRole));
              if (!hasVisibleItems) return null;

              const isExpanded = expandedGroups[group.key];
              const IconComponent = group.icon;
              const hasActiveChild = group.items.some(item => isActive(item.path));

              return (
                <div key={group.key} className="w-full">
                  <Button
                    onClick={() => toggleGroup(group.key)}
                    variant="neutral"
                    padding="p-[12px]"
                    className={`box-border content-stretch flex gap-[8px] items-center relative rounded-[4px] shrink-0 w-[216px] transition-colors ${
                      hasActiveChild ? 'bg-[var(--color-primary-light)] hover:bg-[var(--color-primary-light)]' : 'hover:bg-[var(--color-primary-light)]'
                    } bg-transparent`}
                  >
                    <div className="relative shrink-0 size-[24px]">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div
                      className="font-['Roboto'] font-medium relative text-[15px] tracking-[0.5px] flex-1 text-left text-black min-w-0"
                      style={{ fontVariationSettings: "'wdth' 100" }}
                    >
                      <p className="leading-[24px]">{group.label}</p>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 shrink-0" />
                    )}
                  </Button>
                  {isExpanded && (
                    <div className="flex flex-col gap-[2px] mt-[2px] pl-[12px]">
                      {group.items.map((item) => {
                        if (!canShowItem(item.requiredRole)) return null;
                        const active = isActive(item.path);
                        const ItemIcon = item.icon;
                        const isBillingItem = item.path === '/payments';
                        const showBadge = isBillingItem && pendingCount > 0;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`group box-border content-stretch flex gap-[8px] items-center p-[8px] px-[12px] relative rounded-[4px] shrink-0 w-[204px] transition-colors ${
                              active ? 'bg-[var(--color-primary)]' : 'hover:bg-[var(--color-primary-light)]'
                            }`}
                          >
                            <div className="relative shrink-0 size-[20px]">
                              <ItemIcon className={`w-5 h-5 transition-colors ${
                                active ? 'text-[var(--color-primary-text)]' : 'text-black'
                              }`} />
                            </div>
                            <div
                              className={`font-['Roboto'] font-normal leading-[0] relative text-[14px] tracking-[0.5px] overflow-hidden text-ellipsis flex-1 transition-colors ${
                                active ? 'text-[var(--color-primary-text)]' : 'text-black'
                              }`}
                              style={{ fontVariationSettings: "'wdth' 100" }}
                            >
                              <p className="leading-[20px] truncate">{item.label}</p>
                            </div>
                            {showBadge && (
                              <div className="relative shrink-0 flex items-center justify-center h-5 w-5 bg-red-500 text-white text-xs font-semibold rounded-full">
                                {pendingCount}
                              </div>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header - Fixed to top, thinner design with spacing */}
        <div className="px-6 pt-4 pb-2">
          <div className="bg-white box-border content-stretch flex gap-[4px] items-center justify-end px-[24px] py-[12px] relative rounded-[6px] shadow-[0px_1px_3px_0px_rgba(61,49,43,0.1)]">
            <div className="basis-0 content-stretch flex items-baseline font-['Roboto'] font-normal grow leading-[0] min-h-px min-w-px relative shrink-0 gap-3" style={{ fontVariationSettings: "'wdth' 100" }}>
              <div className="relative shrink-0 text-[var(--color-text-primary)] text-[20px]">
                <p className="leading-[28px]">{getActiveItem()?.label || 'Dashboard'}</p>
              </div>
              <div className="relative shrink-0 text-[var(--color-text-muted)] text-[14px] tracking-[0.3px]" style={{ fontVariationSettings: "'wdth' 100" }}>
                <p className="leading-[20px]">{getActiveItem()?.description || 'Real-time clinic operations overview'}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Location Selector - Multi-Location Support */}
              <LocationSelector className="mr-2" />

              {/* Room Indicator */}
              {authService.hasAnyRole(['admin', 'super_admin']) && (
                <Button
                  onClick={() => setShowRoomModal(true)}
                  variant="neutral"
                  padding="px-3 py-2"
                  className="flex items-center gap-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                  title="Change room configuration"
                >
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {roomConfig.roomName || 'No Room Assigned'}
                  </span>
                </Button>
              )}
              {!authService.hasAnyRole(['admin', 'super_admin']) && roomConfig.roomName && (
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 text-gray-700 rounded-lg border border-gray-200">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm font-medium">{roomConfig.roomName}</span>
                </div>
              )}
              <ClockInOutButton />
              {/* Bell Icon Notification for Super Admin */}
              {isSuperAdmin() && (
                <Button
                  onClick={() => setShowApprovalsModal(true)}
                  variant="neutral"
                  padding="p-2"
                  className="relative text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors bg-transparent"
                  title="View pending refund approvals"
                >
                  <Bell className="w-5 h-5" />
                  {pendingCount > 0 && (
                    <div className="absolute -top-1 -right-1 flex items-center justify-center h-5 w-5 bg-red-500 text-white text-xs font-semibold rounded-full">
                      {pendingCount}
                    </div>
                  )}
                </Button>
              )}
              <UserMenu />
            </div>
          </div>
        </div>

        {/* Scrollable content area */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>

      {/* Room Setup Modal */}
      <RoomSetupModal
        isOpen={showRoomModal}
        onClose={() => setShowRoomModal(false)}
        onSuccess={handleRoomConfigUpdate}
        canClose={true}
      />

      {/* Refund Approvals Modal */}
      <RefundApprovalsModal
        isOpen={showApprovalsModal}
        onClose={() => setShowApprovalsModal(false)}
        onApprovalComplete={refreshCount}
      />
    </div>
  );
};

export default Layout;